'use client'

import { useCallback, useEffect, useState } from 'react'

import {
  getPaymentsIdentityFormConfiguration,
  PaymentsIdentityConfigurationModel,
  Status,
} from '@marketplace-web/pay-identity/payments-identity-data'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { navigateToPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  businessAccountFromUserDto,
  getBusinessAccountRepresentativeDetails,
} from '@marketplace-web/business-accounts/business-accounts-data'

import { clickEvent, viewScreenEvent } from '@marketplace-web/pay-identity/pay-identity-kyc-data'

import { MEMBER_PROFILE_URL } from '../../constants/routes'
import KycEducation from '../KycEducation'
import KybFormContent from './KybFormContent'
import useSubmitKybFormRequest, { KybFormData } from './useSubmitKybFormRequest'
import { transformPaymentsIdentityConfiguration } from '../../transformers'

enum KybScreen {
  KybEducation,
  KybForm,
}

const KybForm = () => {
  const { track } = useTracking()
  const { user } = useSession()
  const businessAddress = businessAccountFromUserDto(user)?.address
  const [visibleKybScreen, setVisibleKybScreen] = useState<KybScreen>(KybScreen.KybEducation)
  const [kybFormConfiguration, setKybFormConfiguration] =
    useState<PaymentsIdentityConfigurationModel | null>(null)
  const [kybFormData, setKybFormData] = useState<KybFormData>({
    firstName: null,
    lastName: null,
    birthdate: null,
    address: null,
    ubos: null,
    isLegalRepresentativeUbo: true,
    documents: [],
    identityDocumentExpiryDate: '',
  })

  const { fetch: fetchFormConfiguration, transformedData: transformedConfiguration } = useFetch(
    getPaymentsIdentityFormConfiguration,
    transformPaymentsIdentityConfiguration,
  )

  const {
    data: newConfiguration,
    submitKybForm,
    showGeneralError,
    loading,
    error,
  } = useSubmitKybFormRequest()

  const { fetch: fetchBusinessAccountRepresentative, data: representativeData } = useFetch(
    getBusinessAccountRepresentativeDetails,
  )

  const trackKybScreen = useCallback(() => {
    if (visibleKybScreen === KybScreen.KybEducation) {
      track(viewScreenEvent({ screen: 'kyb_education' }))
    }
    if (visibleKybScreen === KybScreen.KybForm) {
      track(viewScreenEvent({ screen: 'kyb_form' }))
    }
  }, [visibleKybScreen, track])

  useEffect(() => {
    fetchFormConfiguration()
    fetchBusinessAccountRepresentative()
  }, [fetchBusinessAccountRepresentative, fetchFormConfiguration])

  useEffect(() => {
    setKybFormData(prev => ({
      ...prev,
      firstName: representativeData?.business_representative.first_names || '',
      lastName: representativeData?.business_representative.last_name || '',
      birthdate: representativeData?.business_representative.birthday || '',
      address: businessAddress
        ? {
            ...businessAddress,
            countryId: Number(businessAddress.country_id) || null,
          }
        : null,
    }))
  }, [
    businessAddress,
    representativeData?.business_representative.birthday,
    representativeData?.business_representative.first_names,
    representativeData?.business_representative.last_name,
  ])

  useEffect(() => {
    setKybFormData(prev => ({
      ...prev,
      ubos: null,
      documents: prev.documents,
      ...(transformedConfiguration && {
        birthdate: transformedConfiguration.birthdate,
        address: transformedConfiguration.address,
      }),
    }))
    setKybFormConfiguration(transformedConfiguration || null)
  }, [transformedConfiguration])

  useEffect(() => {
    if (newConfiguration) {
      setKybFormConfiguration(newConfiguration)
    }
  }, [newConfiguration])

  useEffect(() => {
    if (!kybFormConfiguration?.status || !user?.id) return
    const isKybFormStatusInvalid = [
      Status.Unverified,
      Status.VerificationFailed,
      Status.VerificationBlocked,
    ].includes(kybFormConfiguration.status)

    if (!isKybFormStatusInvalid) {
      navigateToPage(MEMBER_PROFILE_URL(user.id))
    }
  }, [kybFormConfiguration?.status, user?.id])

  useEffect(() => {
    trackKybScreen()
  }, [trackKybScreen])

  const handleFormSubmit = async (formData: KybFormData) => {
    await submitKybForm({
      kybFormData: formData,
      requiredFields: kybFormConfiguration?.requiredFields || [],
      representativeData: representativeData?.business_representative,
    })

    track(
      clickEvent({
        target: 'submit_kyb',
        screen: 'kyb_form',
      }),
    )
  }

  const showKybFormContent = () => {
    setVisibleKybScreen(KybScreen.KybForm)
  }

  const handleChangeKYBFormData = (data: Partial<KybFormData>) => {
    setKybFormData(prev => ({
      ...prev,
      ...data,
    }))
  }

  if (loading) {
    return <ContentLoader size={ContentLoader.Size.XLarge} />
  }

  return (
    <>
      {visibleKybScreen === KybScreen.KybEducation && (
        <KycEducation onClick={showKybFormContent} isBusiness />
      )}
      {visibleKybScreen === KybScreen.KybForm && (
        <KybFormContent
          kybFormConfiguration={kybFormConfiguration}
          showGeneralError={showGeneralError}
          apiErrors={error}
          loading={loading}
          kybFormData={kybFormData}
          onChangeKYBFormData={handleChangeKYBFormData}
          onSubmit={handleFormSubmit}
        />
      )}
    </>
  )
}

export default KybForm
